import * as React from 'react';

const MENU_GAP_PX = 4;
const MENU_MAX_HEIGHT_PX = 384;
const MENU_MIN_PREFERRED_HEIGHT_PX = 192;
const MENU_Z_INDEX = 1000;
const VIEWPORT_PADDING_PX = 8;
const DIALOG_PORTAL_CONTAINER_SELECTOR = '[data-dialog-portal-container]';

interface ISelectMenuPosition {
	openAbove: boolean;
	portalContainer: HTMLElement | null;
	style: React.CSSProperties;
}

interface IUseSelectMenuPositionParams {
	isOpen: boolean;
	menuRef: React.RefObject<HTMLElement>;
	triggerRef: React.RefObject<HTMLElement>;
}

const CLOSED_MENU_POSITION: ISelectMenuPosition = {
	openAbove: false,
	portalContainer: null,
	style: {},
};

const getPortalContainer = (rootElement: HTMLElement): HTMLElement =>
	rootElement.closest<HTMLElement>(DIALOG_PORTAL_CONTAINER_SELECTOR) ?? document.body;

/**
 * Positions a portaled select menu against its trigger while keeping it inside the viewport.
 */
export const useSelectMenuPosition = ({
	isOpen,
	menuRef,
	triggerRef,
}: IUseSelectMenuPositionParams): ISelectMenuPosition => {
	const [position, setPosition] = React.useState<ISelectMenuPosition>(CLOSED_MENU_POSITION);

	React.useEffect(() => {
		if (!isOpen) {
			setPosition(CLOSED_MENU_POSITION);
			return;
		}

		const triggerElement = triggerRef.current;
		if (!triggerElement) {
			return;
		}

		const portalContainer = getPortalContainer(triggerElement);
		const updatePosition = (): void => {
			const triggerRect = triggerElement.getBoundingClientRect();
			const availableBelow = window.innerHeight - triggerRect.bottom - MENU_GAP_PX - VIEWPORT_PADDING_PX;
			const availableAbove = triggerRect.top - MENU_GAP_PX - VIEWPORT_PADDING_PX;
			const openAbove =
				availableBelow < MENU_MIN_PREFERRED_HEIGHT_PX && availableAbove > availableBelow;
			const availableHeight = openAbove ? availableAbove : availableBelow;
			const maxHeight = Math.max(0, Math.min(MENU_MAX_HEIGHT_PX, availableHeight));
			const containerRect =
				portalContainer === document.body
					? { left: 0, top: 0 }
					: portalContainer.getBoundingClientRect();

			setPosition({
				openAbove,
				portalContainer,
				style: {
					left: triggerRect.left - containerRect.left,
					maxHeight,
					position: portalContainer === document.body ? 'fixed' : 'absolute',
					top:
						(openAbove ? triggerRect.top - MENU_GAP_PX : triggerRect.bottom + MENU_GAP_PX) -
						containerRect.top,
					width: triggerRect.width,
					zIndex: MENU_Z_INDEX,
				},
			});
		};

		let animationFrameId: number | null = null;
		const schedulePositionUpdate = (): void => {
			if (animationFrameId !== null) {
				return;
			}
			animationFrameId = window.requestAnimationFrame(() => {
				animationFrameId = null;
				updatePosition();
			});
		};
		const handleScroll = (event: Event): void => {
			const eventTarget = event.target;
			if (eventTarget instanceof Node && menuRef.current?.contains(eventTarget)) {
				return;
			}
			schedulePositionUpdate();
		};

		updatePosition();
		window.addEventListener('resize', schedulePositionUpdate);
		window.addEventListener('scroll', handleScroll, true);
		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedulePositionUpdate);
		resizeObserver?.observe(triggerElement);

		return () => {
			window.removeEventListener('resize', schedulePositionUpdate);
			window.removeEventListener('scroll', handleScroll, true);
			resizeObserver?.disconnect();
			if (animationFrameId !== null) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, [isOpen, menuRef, triggerRef]);

	return position;
};
